import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Bell, RefreshCw, XCircle, Search, Mail, MessageCircle, Smartphone, MonitorSmartphone, Megaphone } from "lucide-react";

const CHANNELS = [
  { v: "all", l: "Tutti i canali" },
  { v: "in_app", l: "In-app" },
  { v: "email", l: "Email" },
  { v: "whatsapp", l: "WhatsApp" },
  { v: "push", l: "Push" },
];
const STATUSES = [
  { v: "all", l: "Tutti gli stati" },
  { v: "queued", l: "In coda" },
  { v: "sent", l: "Inviato" },
  { v: "delivered", l: "Consegnato" },
  { v: "failed", l: "Fallito" },
  { v: "cancelled", l: "Annullato" },
  { v: "skipped_demo", l: "Saltato (demo)" },
];
const CATEGORIES = [
  { v: "all", l: "Tutte le categorie" },
  { v: "operational", l: "Operative" },
  { v: "marketing", l: "Marketing" },
];

const CH_ICON = { in_app: MonitorSmartphone, email: Mail, whatsapp: MessageCircle, push: Smartphone };
const CH_LABEL = { in_app: "In-app", email: "Email", whatsapp: "WhatsApp", push: "Push" };

const STATUS_STYLE = {
  queued: "text-[hsl(var(--hi-status-info))] bg-[hsl(var(--hi-status-info)/0.12)] border-transparent",
  sent: "text-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold-soft))] border-transparent",
  delivered: "text-[hsl(var(--hi-status-success))] bg-[hsl(var(--hi-status-success)/0.12)] border-transparent",
  failed: "text-destructive bg-destructive/10 border-transparent",
  cancelled: "text-muted-foreground bg-muted border-transparent",
  skipped_demo: "text-muted-foreground bg-muted border-transparent",
};
const STATUS_LABEL = { queued: "In coda", sent: "Inviato", delivered: "Consegnato", failed: "Fallito", cancelled: "Annullato", skipped_demo: "Saltato (demo)" };

const Kpi = ({ label, value, tone }) => (
  <Card className="p-4">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`mt-1 font-heading text-2xl font-semibold tabular ${tone || ""}`}>{value}</div>
  </Card>
);

export default function Notifiche() {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    const params = new URLSearchParams();
    if (channel !== "all") params.set("channel", channel);
    if (status !== "all") params.set("status", status);
    if (category !== "all") params.set("category", category);
    if (q) params.set("q", q);
    try {
      const [c, a] = await Promise.all([
        api.get(`/communications?${params.toString()}`),
        api.get("/comms/analytics?period=month"),
      ]);
      setItems(c.data);
      setStats(a.data);
    } catch (e) {
      toast.error("Errore nel caricamento delle notifiche");
      setItems([]);
    }
  }, [channel, status, category, q]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    try { await api.post(`/communications/${id}/retry`); toast.success("Reinviata (sandbox)"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const cancel = async (id) => {
    try { await api.post(`/communications/${id}/cancel`); toast.success("Annullata"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const kpis = useMemo(() => stats || {}, [stats]);

  return (
    <div>
      <PageHeader title="Centro Notifiche" subtitle="Conferme, promemoria e campagne verso clienti — canali operativi e marketing">
        <Button variant="outline" onClick={load} data-testid="notifiche-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Aggiorna</Button>
      </PageHeader>

      <div className="rounded-lg border border-[hsl(var(--hi-gold)/0.35)] bg-[hsl(var(--hi-gold-soft)/0.4)] px-4 py-2.5 mb-5 text-sm flex items-center gap-2" data-testid="sandbox-banner">
        <Megaphone className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
        Modalità <strong>Sandbox</strong>: i messaggi email/WhatsApp/push non vengono inviati realmente. Gli in-app arrivano nell'app della cliente.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        <Kpi label="Totale (mese)" value={kpis.total ?? "—"} />
        <Kpi label="In coda" value={kpis.queued ?? "—"} tone="text-[hsl(var(--hi-status-info))]" />
        <Kpi label="Inviate" value={kpis.sent ?? "—"} tone="text-[hsl(var(--hi-gold))]" />
        <Kpi label="Consegnate" value={kpis.delivered ?? "—"} tone="text-[hsl(var(--hi-status-success))]" />
        <Kpi label="Fallite" value={kpis.failed ?? "—"} tone="text-destructive" />
        <Kpi label="Saltate (demo)" value={kpis.skipped_demo ?? "—"} />
      </div>

      <Card className="p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, oggetto o testo" className="pl-8" data-testid="notifiche-search" />
          </div>
          <Select value={channel} onValueChange={setChannel}><SelectTrigger className="w-[160px]" data-testid="filter-channel"><SelectValue /></SelectTrigger><SelectContent>{CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select>
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select>
          <Select value={category} onValueChange={setCategory}><SelectTrigger className="w-[170px]" data-testid="filter-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select>
        </div>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Canale</TableHead>
              <TableHead>Messaggio</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items === null && Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={`sk-${i}`}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
            ))}
            {items !== null && items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                <Bell className="h-6 w-6 mx-auto mb-2 opacity-40" /> Nessuna notifica con questi filtri.
              </TableCell></TableRow>
            )}
            {items !== null && items.map((c) => {
              const Icon = CH_ICON[c.channel] || Bell;
              return (
                <TableRow key={c.id} data-testid={`comm-row-${c.id}`}>
                  <TableCell className="text-xs whitespace-nowrap text-muted-foreground">{fmtDateTime(c.created_at)}</TableCell>
                  <TableCell className="font-medium">{c.client_name || "—"}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-sm"><Icon className="h-3.5 w-3.5 text-muted-foreground" />{CH_LABEL[c.channel] || c.channel}</span></TableCell>
                  <TableCell className="max-w-[320px]">
                    {c.subject && <div className="text-sm font-medium truncate">{c.subject}</div>}
                    <div className="text-xs text-muted-foreground truncate">{c.body}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={c.category === "marketing" ? "default" : "secondary"} className="text-[10px] capitalize">
                      {c.category === "marketing" ? "Marketing" : "Operativa"}
                    </Badge>
                  </TableCell>
                  <TableCell><Badge className={`text-[10px] ${STATUS_STYLE[c.status] || "bg-muted"}`}>{STATUS_LABEL[c.status] || c.status}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {(c.status === "failed" || c.status === "queued") && (
                      <Button variant="ghost" size="sm" onClick={() => retry(c.id)} data-testid={`comm-retry-${c.id}`}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Reinvia</Button>
                    )}
                    {c.status === "queued" && (
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => cancel(c.id)} data-testid={`comm-cancel-${c.id}`}><XCircle className="h-3.5 w-3.5 mr-1" /> Annulla</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
